<template>
  <div
    class="tablero-reparaciones"
    :class="{ 'theme-dark': $vuetify.theme.dark }"
  >
    <v-row no-gutters class="tablero-columnas fill-height flex-nowrap">
      <v-col
        v-for="columna in columnasLocales"
        :key="columna.estado"
        :ref="`columna-${columna.estado}`"
        cols="12"
        sm="6"
        md
        class="columna-kanban pa-2"
      >
        <!-- Cabecera de columna -->
        <div
          class="columna-header d-flex align-center justify-space-between mb-2 pa-2 rounded"
          :class="headerClass(columna.estado)"
        >
          <span class="font-weight-bold text-uppercase text-body-2">
            {{ columna.titulo }}
          </span>
          <v-badge
            :content="columna.items.length"
            :color="badgeColor(columna.estado)"
            inline
          />
        </div>

        <!-- Zona de drop -->
        <draggable
          :list="columna.items"
          :group="{ name: 'usados-reparaciones', pull: true, put: columna.estado !== 'recibidos' }"
          :disabled="isMobileLayout || disableDrag"
          class="columna-body"
          :class="{ 'columna-body--scroll': columna.estado === 'recibidos' || columna.estado === 'espera_repuestos' || columna.estado === 'agendado' || columna.estado === 'terceros' || columna.estado === 'en_proceso' }"
          draggable=".reparacion-draggable-item:not(.no-drag)"
          :handle="'.card-drag-handle'"
          ghost-class="ghost"
          chosen-class="chosen"
          drag-class="drag"
          :animation="150"
          :force-fallback="true"
          :fallback-on-body="true"
          @start="onDragStart($event, columna)"
          @change="onDragChange($event, columna)"
        >
          <div
            v-for="item in columna.items"
            :key="cardKey(item)"
            class="reparacion-draggable-item"
          >
            <TarjetaReparacion
              :item="item"
              :permisos="permisos"
              :items-en-proceso="itemsEnProceso"
              :aplicaciones-en-proceso="aplicacionesEnProceso"
              :categoria-columna="categoriaParaColumna(columna.estado)"
              :disable-drag-handling="disableDrag || item.Estado === 'finalizado'"
              :cursor-pointer="item.Proyeccion === 'en_proceso'"
              @agregar-concepto="$emit('agregar-concepto', { item })"
              @editar-item="$emit('editar-item', { item, ...$event })"
              @eliminar-item="$emit('eliminar-item', { item, ...$event })"
              @ver-comentarios="$emit('ver-comentarios', { item, ...$event })"
              @crear-aplicacion="$emit('crear-aplicacion', { item, ...$event })"
              @completar-item="$emit('completar-item', { item, ...$event })"
              @card-click="$emit('card-click', $event)"
              @ver-historial="$emit('ver-historial', $event)"
              @agendar="$emit('agendar', $event)"
              @gestionar-turnos="$emit('gestionar-turnos', $event)"
              @reprogramar="$emit('reprogramar', $event)"
              @cancelar="$emit('cancelar', $event)"
              @finalizar="$emit('finalizar', $event)"
              @limpiar="$emit('limpiar', $event)"
              @reenviar-email="$emit('reenviar-email', $event)"
              @ver-detalle-orden="$emit('ver-detalle-orden', $event)"
              @cargar-peritaje="$emit('cargar-peritaje', $event)"
              @ver-peritaje="$emit('ver-peritaje', $event)"
            />
          </div>
          <!-- Placeholder cuando la columna está vacía -->
          <div
            v-if="columna.items.length === 0"
            class="text-center text-caption grey--text pa-4"
          >
            Sin unidades
          </div>
        </draggable>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import TarjetaReparacion from "./TarjetaReparacion.vue";

export default {
  name: "TableroReparaciones",
  components: {
    draggable,
    TarjetaReparacion,
  },
  props: {
    columnas: {
      type: Array,
      required: true,
      validator: (val) =>
        val.every(
          (c) =>
            c.titulo && c.estado && Array.isArray(c.items)
        ),
    },
    permisos: {
      type: Object,
      default: () => ({ lectura: false, gestion: false, repuestos: false, destinatarios: false }),
    },
    itemsEnProceso: {
      type: Array,
      default: () => [],
    },
    aplicacionesEnProceso: {
      type: Array,
      default: () => [],
    },
    disableDrag: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      columnasLocales: [],
      isMobileLayout: false,
      ultimoDragRemovido: null,
      dragOrigen: null,
      dragChangeEmitido: false,
    };
  },
  watch: {
    columnas: {
      immediate: true,
      deep: true,
      handler(val) {
        this.columnasLocales = JSON.parse(JSON.stringify(val));
      },
    },
  },
  methods: {
    updateMobileLayout() {
      this.isMobileLayout =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 599px)").matches;
    },
    headerClass(estado) {
      const isDark = this.$vuetify.theme.dark;
      const map = {
        recibidos: isDark ? "blue-grey darken-3" : "blue-grey lighten-4",
        espera_repuestos: isDark ? "amber darken-3" : "amber lighten-4",
        agendado: isDark ? "blue darken-3" : "blue lighten-4",
        en_proceso: isDark ? "purple darken-3" : "purple lighten-4",
        terceros: isDark ? "deep-purple darken-3" : "deep-purple lighten-4",
      };
      return map[estado] || "";
    },
    badgeColor(estado) {
      const isDark = this.$vuetify.theme.dark;
      const map = {
        recibidos: isDark ? "blue-grey lighten-1" : "blue-grey",
        espera_repuestos: isDark ? "amber lighten-1" : "warning",
        agendado: isDark ? "blue lighten-1" : "info",
        en_proceso: isDark ? "purple lighten-1" : "purple",
        terceros: isDark ? "deep-purple lighten-1" : "deep-purple",
      };
      return map[estado] || "grey";
    },
    categoriaParaColumna(estado) {
      const map = {
        espera_repuestos: "repuestos",
        agendado: "mano_obra",
        terceros: "terceros",
      };
      return map[estado] || null;
    },
    cardKey(item) {
      if (item.ReparacionId) {
        return `rep-${item.ReparacionId}`;
      }
      return `sai-${Number(item.Empresa ?? 1)}:${Number(item.UsadoID)}`;
    },
    findCardLocal(item) {
      const key = this.cardKey(item);
      for (let columnIndex = 0; columnIndex < this.columnasLocales.length; columnIndex++) {
        const columna = this.columnasLocales[columnIndex];
        const itemIndex = columna.items.findIndex((i) => this.cardKey(i) === key);
        if (itemIndex !== -1) {
          return { columna, columnIndex, itemIndex, item: columna.items[itemIndex] };
        }
      }
      return null;
    },
    moveCardLocal({ item, toEstado, updates = {}, index = 0 }) {
      if (!item || !toEstado) return;
      const destino = this.columnasLocales.find((columna) => columna.estado === toEstado);
      if (!destino) return;

      const key = this.cardKey(item);
      let cardLocal = { ...item };

      this.columnasLocales.forEach((columna) => {
        const itemIndex = columna.items.findIndex((i) => this.cardKey(i) === key);
        if (itemIndex !== -1) {
          cardLocal = { ...columna.items[itemIndex] };
          columna.items.splice(itemIndex, 1);
        }
      });

      const cardActualizada = {
        ...cardLocal,
        ...updates,
        Estado: updates.Estado || toEstado,
      };
      const safeIndex = Math.max(0, Math.min(index, destino.items.length));
      destino.items.splice(safeIndex, 0, cardActualizada);
    },
    updateCardLocal(item, updates = {}) {
      const encontrado = this.findCardLocal(item);
      if (!encontrado) return;
      encontrado.columna.items.splice(encontrado.itemIndex, 1, {
        ...encontrado.item,
        ...updates,
      });
    },
    revertCardLocal(contexto) {
      if (!contexto || !contexto.item || !contexto.fromEstado) return;
      this.moveCardLocal({
        item: contexto.item,
        toEstado: contexto.fromEstado,
        updates: {
          ...contexto.item,
          Estado: contexto.fromEstado,
        },
        index: contexto.oldIndex || 0,
      });
    },
    onDragStart(event, columna) {
      this.ultimoDragRemovido = null;
      this.dragChangeEmitido = false;
      this.dragOrigen = {
        fromEstado: columna.estado,
        oldIndex:
          event && typeof event.oldIndex === "number" ? event.oldIndex : 0,
      };
    },
    onDragChange(event, columna) {
      if (event.removed) {
        this.ultimoDragRemovido = {
          key: this.cardKey(event.removed.element),
          fromEstado: columna.estado,
          oldIndex: event.removed.oldIndex,
        };
        return;
      }

      if (!event.added || this.dragChangeEmitido) return;

      const item = event.added.element;
      const key = this.cardKey(item);
      const dragRemovido = this.ultimoDragRemovido && this.ultimoDragRemovido.key === key
        ? this.ultimoDragRemovido
        : null;
      const dragOrigen = this.dragOrigen;
      const fromEstado = dragRemovido
        ? dragRemovido.fromEstado
        : dragOrigen
          ? dragOrigen.fromEstado
          : !item.ReparacionId
            ? "recibidos"
            : item.Estado;
      const toEstado = columna.estado;

      if (fromEstado && fromEstado !== toEstado) {
        this.dragChangeEmitido = true;
        this.$emit("drag-change", {
          item,
          fromEstado,
          toEstado,
          newIndex: event.added.newIndex,
          oldIndex: dragRemovido
            ? dragRemovido.oldIndex
            : dragOrigen
              ? dragOrigen.oldIndex
              : 0,
        });
      }

      if (dragRemovido) {
        this.ultimoDragRemovido = null;
      }
    },
    scrollToColumn(estado) {
      const ref = this.$refs[`columna-${estado}`];
      const columna = Array.isArray(ref) ? ref[0] : ref;
      const columnaEl = columna && columna.$el ? columna.$el : columna;
      const tableroEl = this.$el;

      if (!columnaEl || !tableroEl) return;

      if (typeof tableroEl.scrollTo === "function") {
        tableroEl.scrollTo({
          left: columnaEl.offsetLeft,
          behavior: "smooth",
        });
        return;
      }

      tableroEl.scrollLeft = columnaEl.offsetLeft;
    },
  },
  mounted() {
    this.updateMobileLayout();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.updateMobileLayout);
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.updateMobileLayout);
    }
  },
};
</script>

<style scoped>
.tablero-reparaciones {
  overflow-x: auto;
  height: 100%;
  max-width: 100%;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}

/* Track transparente en ambos temas; thumb sutil que contrasta con cada fondo */
.tablero-reparaciones::-webkit-scrollbar,
.tablero-reparaciones .columna-body::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.tablero-reparaciones::-webkit-scrollbar-track,
.tablero-reparaciones .columna-body::-webkit-scrollbar-track {
  background: transparent;
}

.tablero-reparaciones::-webkit-scrollbar-thumb,
.tablero-reparaciones .columna-body::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.18);
}

.tablero-reparaciones.theme-dark::-webkit-scrollbar-thumb,
.tablero-reparaciones.theme-dark .columna-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.22);
}

/* Firefox */
.tablero-reparaciones,
.tablero-reparaciones .columna-body {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
}

.tablero-reparaciones.theme-dark,
.tablero-reparaciones.theme-dark .columna-body {
  scrollbar-color: rgba(255, 255, 255, 0.22) transparent;
}

.columna-kanban {
  display: flex;
  flex-direction: column;
  min-width: 260px;
  scroll-snap-align: start;
}

.columna-body {
  flex: 1;
  min-height: 120px;
  border-radius: 4px;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.02);
}

.columna-body--scroll {
  max-height: 70vh;
  overflow-y: auto;
}

.reparaciones-board--gestor-repuestos {
  overflow-x: hidden;
}

.reparaciones-board--gestor-repuestos .columna-kanban {
  min-width: 0;
}

.reparaciones-board--gestor-repuestos .columna-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 8px;
  align-content: start;
}

.reparacion-draggable-item {
  cursor: grab;
}

.reparacion-draggable-item:active {
  cursor: grabbing;
}

.no-drag {
  cursor: default !important;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.chosen {
  cursor: grabbing;
}

.drag {
  cursor: grabbing;
}

@media (max-width: 599px) {
  .tablero-columnas {
    height: auto !important;
  }

  .columna-kanban {
    flex-basis: 100%;
    max-width: 100%;
    min-width: 100%;
    padding: 4px !important;
  }

  .columna-body--scroll {
    max-height: none;
    overflow-y: visible;
  }

  .reparaciones-board--gestor-repuestos .columna-body {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
